import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import i18n from "../../../../../i18n";
import FlaggedEventsIcon from "../../../../../images/reports/flaggedEventsIcon.svg?react";
import NotesIcon from "../../../../../images/reports/notesIcon.svg?react";
import LiveNotesIcon from "../../../../../images/reports/liveNotesIcon.svg?react";
import RoomScanIcon from "../../../../../images/reports/roomScan.svg?react";
import IdIcon from "../../../../../images/reports/idIcon.svg?react";
import WebcamIcon from "../../../../../images/reports/webcamIcon.svg?react";
import ConfigIcon from "../../../../../images/reports/configIcon.svg?react";
import ScratchNoteIcon from "../../../../../images/reports/scratchNotes.svg?react";
import SignatureIcon from "../../../../../images/reports/signatureTab.svg?react";
import ScreenAndAudioIcon from "../../../../../images/reports/screenAndAudio.svg?react";
import ScreenIcon from "../../../../../images/reports/screen.svg?react";
import AudioIcon from "../../../../../images/reports/audio.svg?react";
import ReferenceMaterialIcon from "../../../../../images/reports/referenceMaterialIcon.svg?react";
import { LiveProctoringSessionNote } from "./useVideoEvents";
import { AutomatedProctoringSessionNote } from "./useSessionNotes";
import { ProctoringAssessmentConfigurationV1 } from "@verity/proctoring-option/types";

const SECONDARY_TAB_KEYS = ["flaggedEvents", "notes", "liveNotes"] as const;
const MAIN_TAB_KEYS = [
	"recording",
	"roomScan",
	"config",
	"idPhotos",
	"signature",
	"scratchNote",
	"referenceMaterial",
] as const;
type SecondaryTab = (typeof SECONDARY_TAB_KEYS)[number];
type MainTab = (typeof MAIN_TAB_KEYS)[number];

interface TabType {
	key: string;
	label: string;
	icon?: React.JSX.Element;
	id?: string;
}

type UseResultTabsProps = {
	sessionNotes: AutomatedProctoringSessionNote[];
	visibleLiveNotes: LiveProctoringSessionNote[];
	proctoringOptions?: Partial<ProctoringAssessmentConfigurationV1>;
	hasTypedSignature?: boolean;
	readOnly?: boolean;
	hideConfigurationsTab?: boolean;
};

export default function useResultTabs({
	sessionNotes,
	visibleLiveNotes,
	proctoringOptions,
	hasTypedSignature,
	readOnly,
	hideConfigurationsTab,
}: UseResultTabsProps) {
	const [secondaryTab, setSecondaryTab] = useState<SecondaryTab>("flaggedEvents");
	const [mainTab, setMainTab] = useState<MainTab>("recording");
	const [mainTabOverflow, setMainTabOverflow] = useState<boolean>(false);

	const baseMainTabId = useId();
	const baseSecondaryTabId = useId();
	const mainTabPanelId = useId();
	const secondaryTabPanelId = useId();

	const mainTabsRef = useRef<HTMLDivElement | null>(null);

	// Manually calculating overflow instead of setting scrollButtons = "auto" because "auto" always showed scroll buttons even if no overflow
	useLayoutEffect(() => {
		const el = mainTabsRef.current;
		if (!el) return;

		const scroller = el.querySelector<HTMLElement>(".MuiTabs-scroller");
		const flexContainer = el.querySelector<HTMLElement>(".MuiTabs-flexContainer");

		if (!scroller || !flexContainer) {
			setMainTabOverflow(false);
			return;
		}

		const checkOverflow = () => {
			setMainTabOverflow(flexContainer.scrollWidth > scroller.clientWidth);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(flexContainer);
		resizeObserver.observe(scroller);
		window.addEventListener("resize", checkOverflow);

		return () => {
			window.removeEventListener("resize", checkOverflow);
			resizeObserver.disconnect();
		};
	}, [mainTab]);

	const mainTabIdRecord = useMemo((): Partial<Record<MainTab, string>> => {
		const entries = MAIN_TAB_KEYS.map((category) => [category, `${baseMainTabId}-${category}`] as const);
		return Object.fromEntries(entries);
	}, [baseMainTabId]);

	const secondaryTabIdRecord = useMemo((): Partial<Record<SecondaryTab, string>> => {
		const entries = SECONDARY_TAB_KEYS.map((category) => [category, `${baseSecondaryTabId}-${category}`] as const);
		return Object.fromEntries(entries);
	}, [baseSecondaryTabId]);

	const secondaryTabList = useMemo(() => {
		const tabs: TabType[] = [
			{
				key: "flaggedEvents",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.flaggedEvents"),
				icon: <FlaggedEventsIcon aria-hidden="true" />,
				id: secondaryTabIdRecord["flaggedEvents"],
			},
		];
		// Dropped rather than emptied on a read-only surface: the panel behind it is the note editor, and a
		// tab that opens onto nothing reads as broken (`SPEC-3.md` §10.3).
		if (!readOnly && sessionNotes.length > 0) {
			tabs.push({
				key: "notes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.notes"),
				icon: <NotesIcon aria-hidden="true" />,
				id: secondaryTabIdRecord["notes"],
			});
		}
		if (visibleLiveNotes.length > 0) {
			tabs.push({
				key: "liveNotes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.liveNote"),
				icon: <LiveNotesIcon aria-hidden="true" />,
				// No Id needed since it is the only "tab" on screen -> so it is not a "tab"
			});
		}
		return tabs;
	}, [secondaryTabIdRecord, sessionNotes.length, visibleLiveNotes.length, readOnly]);

	const mainTabList = useMemo((): TabType[] => {
		if (!proctoringOptions) {
			return [];
		}

		const hasWebcamRecording = proctoringOptions.videoRecordingMode !== "off";
		const hasScreenRecording = proctoringOptions.recordScreen;
		const hasAudioRecording = proctoringOptions.audioRecordingMode !== "off";
		const hasScratchNote = proctoringOptions.uploadScratchNotes;
		const tabs: TabType[] = [];

		if (hasWebcamRecording && hasScreenRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording && hasScreenRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndAudio"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasScreenRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <ScreenAndAudioIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screenAndAudio"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcam"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasScreenRecording) {
			tabs.push({
				key: "recording",
				icon: <ScreenIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screen"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <AudioIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.audio"),
				id: mainTabIdRecord["recording"],
			});
		}

		if (proctoringOptions?.scanRoom) {
			tabs.push({
				key: "roomScan",
				icon: <RoomScanIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.roomScan"),
				id: mainTabIdRecord["roomScan"],
			});
		}
		if (proctoringOptions?.idVerification) {
			tabs.push({
				key: "idPhotos",
				icon: <IdIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.idPhotos"),
				id: mainTabIdRecord["idPhotos"],
			});
		}
		if (proctoringOptions?.uploadSignature && !hasTypedSignature) {
			tabs.push({
				key: "signature",
				icon: <SignatureIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.signature.tab"),
				id: mainTabIdRecord["signature"],
			});
		}
		if (hasScratchNote) {
			tabs.push({
				key: "scratchNote",
				icon: <ScratchNoteIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.tab"),
				id: mainTabIdRecord["scratchNote"],
			});
		}

		if (!hideConfigurationsTab && Object.keys(proctoringOptions).length > 0) {
			tabs.push({
				key: "config",
				icon: <ConfigIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.configurations"),
				id: mainTabIdRecord["config"],
			});
		}

		if (proctoringOptions?.referenceMaterialMode === "student") {
			tabs.push({
				key: "referenceMaterial",
				icon: <ReferenceMaterialIcon aria-hidden="true" />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.referenceMaterial.tab"),
				id: mainTabIdRecord["referenceMaterial"],
			});
		}
		return tabs;
	}, [mainTabIdRecord, proctoringOptions, hasTypedSignature, hideConfigurationsTab]);

	const handleMainTabChange = useCallback((newTab: MainTab) => {
		setMainTab(newTab);
	}, []);

	const handleSecondaryTabChange = useCallback((newTab: SecondaryTab) => {
		setSecondaryTab(newTab);
	}, []);

	// Reset to first tab when tab list changes
	useEffect(() => {
		setMainTab((mainTabList[0]?.key as MainTab) || "recording");
	}, [mainTabList]);

	return {
		mainTab,
		secondaryTab,
		mainTabList,
		secondaryTabList,
		handleMainTabChange,
		handleSecondaryTabChange,
		mainTabOverflow,
		mainTabsRef,
		mainTabIdRecord,
		secondaryTabIdRecord,
		mainTabPanelId,
		secondaryTabPanelId,
	};
}
